import { FileText, Sparkles } from 'lucide-react';
import type { CoachStarter } from '../../../api/types';

interface Props {
  scopeLabel: string;
  starters: CoachStarter[];
  loading?: boolean;
  onPick: (s: CoachStarter) => void;
}

/** A new chat: what the coach can do here, questions built from this scope's data, and reports. */
export default function CoachEmptyState({ scopeLabel, starters, loading, onPick }: Props) {
  const questions = starters.filter((s) => s.kind === 'question');
  const reports = starters.filter((s) => s.kind === 'report');
  return (
    <div className="flex flex-1 flex-col justify-center overflow-y-auto px-5 py-4">
      <div className="mx-auto w-full max-w-sm">
        <span aria-hidden className="mb-3 grid h-11 w-11 place-items-center rounded-[14px] bg-accent-bg text-accent-ink">
          <Sparkles size={20} />
        </span>
        <h4 className="font-display text-lg font-bold text-ink">Ask about {scopeLabel}</h4>
        <p className="mt-1 text-[13px] text-muted-2">
          The coach looks up exams, chapters and students as it answers, shows its sources, and can
          draft notes, cases and messages for you to confirm.
        </p>
        {loading ? <p className="mt-4 text-[13px] text-muted">Loading suggestions…</p> : (
          <>
            {questions.length > 0 && (
              <div className="mt-4 flex flex-col gap-2">
                {questions.map((s, i) => (
                  <button key={i} type="button" onClick={() => onPick(s)}
                    className="rounded-[12px] border border-line px-3 py-2 text-left text-[13px] text-ink hover:bg-surface-2">
                    {s.label}
                  </button>
                ))}
              </div>
            )}
            {reports.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Reports</p>
                <div className="flex flex-wrap gap-2">
                  {reports.map((s, i) => (
                    <button key={i} type="button" onClick={() => onPick(s)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-accent-line bg-accent-bg px-3 py-1 text-[13px] font-semibold text-accent-ink">
                      <FileText size={14} aria-hidden /> {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
